import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Styles
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '30px'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '10px'
  },
  tab: {
    padding: '10px 20px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#718096',
    borderRadius: '8px 8px 0 0',
    transition: 'all 0.2s'
  },
  tabActive: {
    background: '#2563eb',
    color: 'white'
  },
  section: {
    marginBottom: '30px'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '8px'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '12px'
  },
  cardMeta: {
    fontSize: '12px',
    color: '#a0aec0',
    display: 'flex',
    gap: '16px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #e2e8f0',
    color: '#4a5568',
    fontSize: '14px',
    fontWeight: '600'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #e2e8f0',
    color: '#2d3748',
    fontSize: '14px'
  },
  button: {
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  buttonSmall: {
    padding: '6px 12px',
    fontSize: '12px',
    marginRight: '8px'
  },
  buttonSecondary: {
    background: '#e2e8f0',
    color: '#4a5568'
  },
  buttonDanger: {
    background: '#fc8181',
    color: 'white'
  },
  buttonSuccess: {
    background: '#48bb78',
    color: 'white'
  },
  buttonWarning: {
    background: '#ed8936',
    color: 'white'
  },
  form: {
    background: 'white',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '80px',
    resize: 'vertical'
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    background: 'white'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '600'
  },
  badgeDanger: {
    background: '#fed7d7',
    color: '#c53030'
  },
  badgeWarning: {
    background: '#fefcbf',
    color: '#b7791f'
  },
  badgeSuccess: {
    background: '#c6f6d5',
    color: '#276749'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto'
  },
  flaggedMessage: {
    background: '#fff5f5',
    borderLeft: '4px solid #fc8181'
  },
  messageContent: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    maxHeight: '100px',
    overflow: 'hidden'
  }
};

// Category Management Component
const CategoryManagement = ({ categories, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ category_name: '', description: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_name.trim()) {
      setError('Category name is required');
      return;
    }
    try {
      if (editingCategory) {
        await api.put(`/discussion-categories/${editingCategory.category_id}`, formData);
      } else {
        await api.post('/discussion-categories', formData);
      }
      setShowForm(false);
      setEditingCategory(null);
      setFormData({ category_name: '', description: '' });
      onRefresh();
    } catch (err) {
      setError('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ 
      category_name: category.category_name, 
      description: category.description || '' 
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? All groups and topics within it will also be deleted.')) {
      try {
        await api.delete(`/discussion-categories/${categoryId}`);
        onRefresh();
      } catch (err) {
        setError('Failed to delete category');
      }
    }
  };

  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Discussion Categories</h2>
        <button 
          style={styles.button}
          onClick={() => { setShowForm(true); setEditingCategory(null); setFormData({ category_name: '', description: '' }); }}
        >
          + Add Category
        </button>
      </div>

      {showForm && (
        <form style={styles.form} onSubmit={handleSubmit}>
          <h3 style={{ marginBottom: '20px' }}>
            {editingCategory ? 'Edit Category' : 'Create New Category'}
          </h3>
          {error && <div style={{ color: '#fc8181', marginBottom: '12px' }}>{error}</div>}
          <div style={styles.formGroup}>
            <label style={styles.label}>Category Name *</label>
            <input
              style={styles.input}
              type="text"
              value={formData.category_name}
              onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
              placeholder="Enter category name"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              style={styles.textarea}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe this category"
            />
          </div>
          <div style={styles.buttonGroup}>
            <button type="submit" style={styles.button}>
              {editingCategory ? 'Update Category' : 'Create Category'}
            </button>
            <button 
              type="button" 
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={() => { setShowForm(false); setEditingCategory(null); setError(''); }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Category Name</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Groups</th>
              <th style={styles.th}>Sort Order</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <tr key={cat.category_id}>
                <td style={styles.td}>{cat.category_name}</td>
                <td style={styles.td}>{cat.description || '-'}</td>
                <td style={styles.td}>{cat.group_count || 0}</td>
                <td style={styles.td}>{cat.sort_order || 0}</td>
                <td style={styles.td}>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonSecondary }}
                    onClick={() => handleEdit(cat)}
                  >
                    Edit
                  </button>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                    onClick={() => handleDelete(cat.category_id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#718096' }}>
                  No categories found. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Group Management Component
const GroupManagement = ({ groups, categories, onRefresh }) => {
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editFormData, setEditFormData] = useState({ group_name: '', description: '' });
  const [imageUploading, setImageUploading] = useState(false);
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [formData, setFormData] = useState({ group_name: '', description: '', category_id: '' });
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');

  const handleEditOpen = (group) => {
    setEditingGroup({ ...group });
    setEditFormData({ group_name: group.group_name, description: group.description || '' });
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editFormData.group_name.trim()) {
      setEditError('Group name is required');
      return;
    }
    try {
      await api.put(`/discussion-groups/${editingGroup.group_id}`, {
        group_name: editFormData.group_name,
        description: editFormData.description
      });
      setShowEditModal(false);
      setEditingGroup(null);
      onRefresh();
    } catch (err) {
      setEditError('Failed to save group');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('image', file);
    setImageUploading(true);
    try {
      await api.post(`/discussion-groups/${editingGroup.group_id}/image`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingGroup(prev => ({ ...prev, has_image: true }));
    } catch (err) {
      setEditError('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm('Remove this group image?')) return;
    try {
      await api.delete(`/discussion-groups/${editingGroup.group_id}/image`);
      setEditingGroup(prev => ({ ...prev, has_image: false }));
    } catch (err) {
      setEditError('Failed to delete image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.group_name.trim()) {
      setError('Group name is required');
      return;
    }
    if (!formData.category_id) {
      setError('Please select a category');
      return;
    }
    try {
      await api.post('/discussion-groups', {
        group_name: formData.group_name,
        description: formData.description,
        category_id: parseInt(formData.category_id)
      });
      setShowForm(false);
      setFormData({ group_name: '', description: '', category_id: '' });
      onRefresh();
    } catch (err) {
      setError('Failed to create group');
    }
  };

  const handleMove = async () => {
    if (!targetCategoryId) {
      setError('Please select a target category');
      return;
    }
    try {
      await api.put(`/discussion-groups/${selectedGroup.group_id}/move`, {
        category_id: parseInt(targetCategoryId)
      });
      setShowMoveModal(false);
      setSelectedGroup(null);
      setTargetCategoryId('');
      onRefresh();
    } catch (err) {
      setError('Failed to move group');
    }
  };

  const handleDelete = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this group? All topics and messages within it will also be deleted.')) {
      try {
        await api.delete(`/discussion-groups/${groupId}`);
        onRefresh();
      } catch (err) {
        setError('Failed to delete group');
      }
    }
  };

  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Discussion Groups</h2>
        <button 
          style={styles.button}
          onClick={() => { setShowForm(true); setFormData({ group_name: '', description: '', category_id: '' }); }}
        >
          + Add Group
        </button>
      </div>

      {error && (
        <div style={{ color: '#fc8181', marginBottom: '12px', padding: '12px', background: '#fff5f5', borderRadius: '8px' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {showForm && (
        <form style={styles.form} onSubmit={handleSubmit}>
          <h3 style={{ marginBottom: '20px' }}>Create New Discussion Group</h3>
          <div style={styles.formGroup}>
            <label style={styles.label}>Category *</label>
            <select
              style={styles.select}
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Group Name *</label>
            <input
              style={styles.input}
              type="text"
              value={formData.group_name}
              onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
              placeholder="Enter group name"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              style={styles.textarea}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this group is about"
            />
          </div>
          <div style={styles.buttonGroup}>
            <button type="submit" style={styles.button}>Create Group</button>
            <button 
              type="button" 
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={() => { setShowForm(false); setError(''); }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Group Name</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Topics</th>
              <th style={styles.th}>Created By</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(group => (
              <tr key={group.group_id}>
                <td style={styles.td}>{group.group_name}</td>
                <td style={styles.td}>{group.category_name || 'Unknown'}</td>
                <td style={styles.td}>{group.topic_count || 0}</td>
                <td style={styles.td}>{group.creator_display_name || 'Unknown'}</td>
                <td style={styles.td}>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonSecondary }}
                    onClick={() => handleEditOpen(group)}
                  >
                    Edit
                  </button>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonWarning }}
                    onClick={() => { setSelectedGroup(group); setShowMoveModal(true); }}
                  >
                    Move
                  </button>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                    onClick={() => handleDelete(group.group_id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr>
                <td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#718096' }}>
                  No groups found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showEditModal && editingGroup && (
        <div style={styles.modal} onClick={() => setShowEditModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px' }}>Edit Group: {editingGroup.group_name}</h3>
            {editError && (
              <div style={{ color: '#fc8181', marginBottom: '12px', padding: '10px', background: '#fff5f5', borderRadius: '6px' }}>
                {editError}
                <button onClick={() => setEditError('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Group Name *</label>
              <input
                style={styles.input}
                type="text"
                value={editFormData.group_name}
                onChange={(e) => setEditFormData({ ...editFormData, group_name: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Group Icon Image</label>
              {editingGroup.has_image ? (
                <div style={{ marginBottom: '12px' }}>
                  <img
                    src={`${API_URL}/discussion-groups/${editingGroup.group_id}/image`}
                    alt="Group icon"
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', display: 'block', marginBottom: '8px' }}
                  />
                  <button
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                    onClick={handleImageDelete}
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <p style={{ fontSize: '13px', color: '#718096', marginBottom: '8px' }}>No image set.</p>
              )}
              <div>
                <label style={{ ...styles.button, ...styles.buttonSmall, cursor: 'pointer', display: 'inline-block' }}>
                  {imageUploading ? 'Uploading…' : editingGroup.has_image ? 'Replace Image' : 'Upload Image'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={imageUploading} />
                </label>
              </div>
            </div>
            <div style={styles.buttonGroup}>
              <button style={styles.button} onClick={handleEditSave}>Save Changes</button>
              <button
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={() => { setShowEditModal(false); setEditingGroup(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showMoveModal && (
        <div style={styles.modal} onClick={() => setShowMoveModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px' }}>Move Group</h3>
            <p style={{ marginBottom: '16px', color: '#718096' }}>
              Move "{selectedGroup?.group_name}" to a different category:
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Target Category</label>
              <select
                style={styles.select}
                value={targetCategoryId}
                onChange={(e) => setTargetCategoryId(e.target.value)}
              >
                <option value="">Select a category</option>
                {categories.filter(c => c.category_id !== selectedGroup?.category_id).map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.category_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.buttonGroup}>
              <button style={styles.button} onClick={handleMove}>Move Group</button>
              <button 
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={() => { setShowMoveModal(false); setSelectedGroup(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Topic Management Component  
const TopicManagement = ({ topics, groups, onRefresh }) => {
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [targetGroupId, setTargetGroupId] = useState('');
  const [error, setError] = useState('');

  const handleMove = async () => {
    if (!targetGroupId) {
      setError('Please select a target group');
      return;
    }
    try {
      await api.put(`/discussion-topics/${selectedTopic.topic_id}/move`, {
        group_id: parseInt(targetGroupId)
      });
      setShowMoveModal(false);
      setSelectedTopic(null);
      setTargetGroupId('');
      onRefresh();
    } catch (err) {
      setError('Failed to move topic');
    }
  };

  const handleDelete = async (topicId) => {
    if (window.confirm('Are you sure you want to delete this topic? All messages within it will also be deleted.')) {
      try {
        await api.delete(`/discussion-topics/${topicId}`);
        onRefresh();
      } catch (err) {
        setError('Failed to delete topic');
      }
    }
  };

  return (
    <div style={styles.section}>
      <h2 style={{ marginBottom: '20px' }}>Discussion Topics</h2>

      {error && (
        <div style={{ color: '#fc8181', marginBottom: '12px', padding: '12px', background: '#fff5f5', borderRadius: '8px' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Topic Title</th>
              <th style={styles.th}>Group</th>
              <th style={styles.th}>Messages</th>
              <th style={styles.th}>Created By</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {topics.map(topic => (
              <tr key={topic.topic_id}>
                <td style={styles.td}>{topic.title}</td>
                <td style={styles.td}>{topic.group_name || 'Unknown'}</td>
                <td style={styles.td}>{topic.message_count || 0}</td>
                <td style={styles.td}>{topic.creator_display_name || 'Unknown'}</td>
                <td style={styles.td}>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonWarning }}
                    onClick={() => { setSelectedTopic(topic); setShowMoveModal(true); }}
                  >
                    Move
                  </button>
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                    onClick={() => handleDelete(topic.topic_id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {topics.length === 0 && (
              <tr>
                <td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#718096' }}>
                  No topics found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showMoveModal && (
        <div style={styles.modal} onClick={() => setShowMoveModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px' }}>Move Topic</h3>
            <p style={{ marginBottom: '16px', color: '#718096' }}>
              Move "{selectedTopic?.title}" to a different group:
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Target Group</label>
              <select
                style={styles.select}
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
              >
                <option value="">Select a group</option>
                {groups.filter(g => g.group_id !== selectedTopic?.group_id).map(group => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.buttonGroup}>
              <button style={styles.button} onClick={handleMove}>Move Topic</button>
              <button 
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={() => { setShowMoveModal(false); setSelectedTopic(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Flagged Messages Component
const FlaggedMessages = ({ messages, onRefresh }) => {
  const [error, setError] = useState('');

  const handleHide = async (messageId, shouldHide) => {
    try {
      await api.put(`/discussion-messages/${messageId}/hide`, { is_hidden: shouldHide });
      onRefresh();
    } catch (err) {
      setError('Failed to update message visibility');
    }
  };

  const handleDelete = async (messageId) => {
    if (window.confirm('Are you sure you want to permanently delete this message?')) {
      try {
        await api.delete(`/discussion-messages/${messageId}`);
        onRefresh();
      } catch (err) {
        setError('Failed to delete message');
      }
    }
  };

  return (
    <div style={styles.section}>
      <h2 style={{ marginBottom: '20px' }}>Flagged Messages</h2>

      {error && (
        <div style={{ color: '#fc8181', marginBottom: '12px', padding: '12px', background: '#fff5f5', borderRadius: '8px' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {messages.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', color: '#718096' }}>
          <p>No flagged messages! The community is behaving well.</p>
        </div>
      ) : (
        messages.map(msg => (
          <div key={msg.message_id} style={{ ...styles.card, ...styles.flaggedMessage }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ ...styles.badge, ...styles.badgeDanger }}>
                    Flagged
                  </span>
                  {msg.is_hidden && (
                    <span style={{ ...styles.badge, ...styles.badgeWarning }}>
                      Hidden
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '8px' }}>
                  Posted by {msg.display_name || 'Unknown'} in "{msg.topic_title || 'Unknown Topic'}"
                </div>
                <div style={styles.messageContent}>
                  {msg.message_content}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
                {msg.is_hidden ? (
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonSuccess }}
                    onClick={() => handleHide(msg.message_id, false)}
                  >
                    Unhide
                  </button>
                ) : (
                  <button 
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonWarning }}
                    onClick={() => handleHide(msg.message_id, true)}
                  >
                    Hide
                  </button>
                )}
                <button 
                  style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                  onClick={() => handleDelete(msg.message_id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Main Admin Screen Component
const DiscussionAdminScreen = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/discussion-categories');
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/discussion-groups');
      setGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      const res = await api.get('/discussion-topics');
      setTopics(res.data);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  }, []);

  const fetchFlaggedMessages = useCallback(async () => {
    try {
      const res = await api.get('/discussion-messages/flagged');
      setFlaggedMessages(res.data);
    } catch (err) {
      console.error('Failed to fetch flagged messages:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchCategories(),
      fetchGroups(),
      fetchTopics(),
      fetchFlaggedMessages()
    ]);
    setLoading(false);
  }, [fetchCategories, fetchGroups, fetchTopics, fetchFlaggedMessages]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>Access Denied</h2>
          <p style={{ color: '#718096' }}>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>Discussion Administration</h1>
        <p style={{ color: '#718096', marginTop: '8px' }}>
          Manage categories, groups, topics, and moderate content
        </p>
      </div>

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'categories' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'groups' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('groups')}
        >
          Groups
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'topics' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('topics')}
        >
          Topics
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'flagged' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('flagged')}
        >
          Flagged Messages 
          {flaggedMessages.length > 0 && (
            <span style={{ 
              marginLeft: '8px', 
              background: '#fc8181', 
              color: 'white', 
              padding: '2px 8px', 
              borderRadius: '10px',
              fontSize: '11px'
            }}>
              {flaggedMessages.length}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          Loading...
        </div>
      ) : (
        <>
          {activeTab === 'categories' && (
            <CategoryManagement categories={categories} onRefresh={fetchAll} />
          )}
          {activeTab === 'groups' && (
            <GroupManagement groups={groups} categories={categories} onRefresh={fetchAll} />
          )}
          {activeTab === 'topics' && (
            <TopicManagement topics={topics} groups={groups} onRefresh={fetchAll} />
          )}
          {activeTab === 'flagged' && (
            <FlaggedMessages messages={flaggedMessages} onRefresh={fetchAll} />
          )}
        </>
      )}
    </div>
  );
};

export default DiscussionAdminScreen;
